import { Component, Inject, Input, Optional, TemplateRef, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { IClientTankDevice, IClientTankHost } from '@services/grid.service';
import { LogService } from '@services/log.service';
import { SecurityPolicies } from '@services/auth-variables';
import { AppDialogService } from '@services/app-dialog.service';
import { BeforeOpenPayload } from 'src/app/modules/tank-move/components/tank-move-button/tank-move-button.component';
import { deviceTypeCategory } from '../../interfaces/deviceTypeCategory';

export interface DialogData {
  tankInfo: IClientTankHost;
  dialogType: 'host' | 'gateway'; // 'host' for Host and Clients, 'gateway' for Gateway and Sensors
}

@Component({
  selector: 'app-client-tank-list-dialog',
  templateUrl: './client-tank-list-dialog.component.html',
  styleUrls: ['./client-tank-list-dialog.component.css'],
})
export class ClientTankListDialogComponent {
  @ViewChild('moveConfirmationTemplate', { static: true })
  moveConfirmationTemplate!: TemplateRef<any>;
  private _datasource: DialogData | undefined;
  protected SecurityPolicies = SecurityPolicies;
  public get datasource(): DialogData | undefined {
    return this._datasource;
  }

  get isGatewayDialog(): boolean {
    return this.datasource?.dialogType === 'gateway';
  }

  get skyTankSensorCount(): number {
    if (this.isGatewayDialog) {
      return this.sensorTankList.length;
    }

    return this.datasource?.tankInfo?.skyTankSensorCount || 0;
  }

  get st90Count(): number {
    if (this.isGatewayDialog) {
      return this.st90TankList.length;
    }

    return this.datasource?.tankInfo?.st90Count || 0;
  }

  get connectionCountLabel(): string {
    return this._ts.instant('TankGrid.ConnectionCount');
  }

  get skyTankSensorLabel(): string {
    return this._ts.instant('TankPage.KeyTankInfo.SkyTankSensors');
  }

  get st90ClientLabel(): string {
    return this._ts.instant('TankPage.KeyTankInfo.ST90Clients');
  }
  @Input()
  public set datasource(value: DialogData | undefined) {
    this._datasource = value;
    const list = this.datasource?.tankInfo.clientTankList ?? [];

    if (this.isGatewayDialog) {
      const sortedList = [...list].sort((a, b) =>
        (a.tankName ?? '').localeCompare(b.tankName ?? '', undefined, { sensitivity: 'base' })
      );

      this.sensorTankList = sortedList.filter(
        x => x.deviceCategory === deviceTypeCategory.skyTankSensor
      );
      //ST90 is every non-sensor client, not just legacyClient - this has to stay the complement
      //of the sensor filter so it matches GridService.ST90Count, which the non-gateway branch
      //below reads straight off the API. Whitelisting legacyClient dropped radar from both lists.
      this.st90TankList = sortedList.filter(
        x => x.deviceCategory !== deviceTypeCategory.skyTankSensor
      );
      this.tankList = sortedList;
      return;
    }

    this.sensorTankList = [];
    this.st90TankList = [];
    this.tankList = list;
  }

  tankList: IClientTankDevice[] = [];
  sensorTankList: IClientTankDevice[] = [];
  st90TankList: IClientTankDevice[] = [];
  anUnassignOccurred: boolean = false;

  // Flag to gate action buttons - will be enabled in later JIRA ticket
  showActionButtons = true;
  isDialog = this.dialogRef ? true : false;

  _allColumns = [
    { key: 'clientName', type: 'string' },
    { key: 'rtuNumber', type: 'string' },
    { key: 'minNumber', type: 'string' },
    { key: 'status', type: 'string' },
    { key: 'locationFormatted', type: 'string' },
    { key: 'deviceModel', type: 'string' },
    { key: 'lastInventoryTime', type: 'string' },
    { key: 'actions', type: 'string' },
  ];
  exportToExcelFileName = 'Host_Client_Connections_Export';

  moveButtonMessage = this._ts.instant('MoveForms.TankMove.MoveButtonMessage');
  cancelButtonMessage = this._ts.instant('MoveForms.TankMove.CancelButtonMessage');

  constructor(
    @Optional() @Inject(MAT_DIALOG_DATA) public data: DialogData | undefined,
    @Optional() private dialogRef: MatDialogRef<ClientTankListDialogComponent>,
    private _ts: TranslateService,
    private logger: LogService,
    private appDialogService: AppDialogService
  ) {
    if (this.data) {
      this.datasource = this.data;
    }
    this.initializeForm();
  }

  get dialogTitle(): string {
    return this.datasource?.dialogType === 'gateway'
      ? 'TankGrid.SkytankGatewayConnections'
      : 'TankGrid.AssignedClients';
  }

  get exportFileName(): string {
    if (this.datasource?.dialogType === 'gateway') {
      return 'Gateway_Sensor_Connections_Export';
    }
    return 'Host_Client_Connections_Export';
  }

  getDisplayedColumns(): string[] {
    const baseColumns = [
      'clientName',
      'rtuNumber',
      'minNumber',
      'status',
      'locationFormatted',
      'deviceModel',
      'lastInventoryTime',
    ];

    if (this.showActionButtons) {
      return [...baseColumns, 'actions'];
    }
    return baseColumns;
  }

  onCloseButtonClick() {
    this.dialogRef.close();
    if (this.anUnassignOccurred) this.refreshPage();
  }

  onBeforeOpen(event: BeforeOpenPayload) {
    const client = event.context as IClientTankDevice;
    if (this.isClientLocationSameAsHost(client)) return;

    event.cancel = true; // Cancel the default move action
    debugger;
    const isSensor: boolean = client?.statusString == 'Connected';
    const isLocationShipTo: boolean = client?.locationIsReceiving;
    var confirmationTitle: string = '';
    var confirmationMessage: string = '';

    if (isSensor) {
      confirmationTitle = this._ts.instant('MoveForms.TankMove.SensorAndGatewayPairing')
      confirmationMessage = isLocationShipTo
        ? this._ts.instant('MoveForms.TankMove.ConfirmSensorMoveMessage.ReceivingLocation')
        : this._ts.instant('MoveForms.TankMove.ConfirmSensorMoveMessage');

    } else {
      confirmationTitle = this._ts.instant('MoveForms.TankMove.ClientAndHostPairing')
      confirmationMessage = this._ts.instant('MoveForms.TankMove.ConfirmClientMoveMessage')
    }

    this.appDialogService
      .showConfirmationDialog(
        confirmationTitle,
        confirmationMessage,
        null,
        this.cancelButtonMessage,
        this.moveConfirmationTemplate
      )
      .subscribe(confirmation => {
        if (!confirmation) return;

        if (this.dialogRef) this.dialogRef.close(); // Close the dialog before proceeding
        event.proceed(); // Proceed with the move action if confirmed
      });
  }

  onUnassignComplete(tankId: number) {
    // mark that an unassign has occurred
    this.anUnassignOccurred = true;

    // get the device id that was just unassigned
    const unassignedDeviceId = this.tankList.find(x => x.tankId == tankId)?.deviceId;

    // if tank was disconnected/unassigned, remove it from the list of tanks
    this.tankList = this.tankList.filter(x => x.tankId != tankId);
    this.sensorTankList = this.sensorTankList.filter(x => x.tankId != tankId);
    this.st90TankList = this.st90TankList.filter(x => x.tankId != tankId);

    // immediately refresh the page if there are no more assigned tanks remaining, or
    // if the unassigned tank was the initiating device id
    if (
      this.tankList.length == 0 ||
      unassignedDeviceId == this.datasource?.tankInfo.initiatingDeviceId
    ) {
      this.refreshPage();
    }
  }

  refreshPage() {
    // reload the page to reflect changes, using a hard cache buster reload to avoid caching issues
    window.location.href = window.location.href.split('?')[0] + '?cache=' + new Date().getTime();
    window.location.reload();
  }

  onClientNameClick(client: IClientTankDevice) {
    // Handle client name click - navigate to details
    this.logger.log('Client name clicked:', client);
  }

  protected isClientLocationSameAsHost(client: IClientTankDevice): boolean {
    return client?.locationId === this?.datasource.tankInfo?.locationId;
  }

  private initializeForm() {
    if (this.dialogRef) this.dialogRef.disableClose = true;
  }

  async exportToExcel() {
    if (!this.tankList || this.tankList.length == 0) return;

    let dataToExport: any[] = [];
    // append host info to each row
    this.tankList.forEach(client => {
      dataToExport.push({
        hostName: this.datasource?.tankInfo.hostName,
        hostRtu: this.datasource?.tankInfo.hostRtu,
        startCallSchedule: this.datasource?.tankInfo.callScheduleStart,
        hostLocationName: this.datasource?.tankInfo.locationName,
        hostLocationId: this.datasource?.tankInfo.locationId,
        hostLocationAddress:
          this.datasource?.tankInfo.locationAddress1 +
          ' ' +
          this.datasource?.tankInfo.locationAddress2,
        hostLocationCity: this.datasource?.tankInfo.locationCity,
        hostLocationState: this.datasource?.tankInfo.locationState,
        tankName: client.tankName,
        rtuNumber: client.rtuNumber?.toString(),
        minNumber: client.min0?.toString(),
        pairedStatus: client.statusString,
        locationId: client.locationId,
        locationName: client.locationName,
        locationAddress: `${client.locationAddress1} ${client.locationAddress2}`.trimEnd(),
        locationCity: client.locationCity,
        locationState: client.locationState,
        deviceModel: client.deviceModel,
        lastInventoryTime: client.lastInventoryTimeConverted,
      });
    });

    const headers = this.generateHeaders(dataToExport);

    this.logger.log('exportToExcel', dataToExport, headers);

    try {
      const XLSX = await import('xlsx');
      var wb = XLSX.utils.book_new();
      var ws = XLSX.utils.json_to_sheet(dataToExport, { header: headers });

      if (this.tankList.length === 0) {
        ws['!ref'] = 'A1:' + XLSX.utils.encode_col(headers.length - 1) + '1';
      }

      if (ws && ws['!ref']) {
        const range = XLSX.utils.decode_range(ws['!ref']);
        for (let C = range.s.c; C <= range.e.c; ++C) {
          const address = XLSX.utils.encode_col(C) + '1';
          if (ws[address] && typeof ws[address].v === 'string') {
            const header = ws[address].v;
            ws[address].v = this.translateHeader(header);
          }
        }
      }

      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
      XLSX.writeFile(wb, `${this.exportFileName}.xlsx`);
    } catch (error) {
      this.logger.error('Error during export:', error);
    }
  }

  generateHeaders(arr: any[]): string[] {
    if (arr.length === 0) {
      return this._allColumns.map(column => column.key);
    }
    return Object.keys(arr[0]);
  }

  translateHeader(header: string): string {
    if (typeof header !== 'string') {
      return header;
    }
    return this._ts.instant(`Column.${this.datasource.dialogType}.${header}`);
  }

  isClientDevice(client): boolean {
    return (
      client.deviceCategory == deviceTypeCategory.skyTankSensor ||
      client.deviceCategory == deviceTypeCategory.legacyClient
    );
    //  return this.datasource?.dialogType == 'gateway' && client.deviceCategory == deviceTypeCategory.skyTankSensor;
  }

}
